import { useState, useRef, useEffect } from "react";
import { COUNTRY_CODES, STATUS_OPTIONS, PHASES, PHASE_DOT, T, mono } from "./utils";

/* ── Inline editable text ── */
export function Editable({ value, onChange, style, placeholder, multiline }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const ref = useRef();
  useEffect(() => setVal(value), [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);
  const commit = () => { setEditing(false); const v = (val || "").trim(); if (v && v !== value) onChange(v); };
  const sh = { ...style, border: `1px solid ${T.accent}`, borderRadius: 2, padding: "1px 5px", background: "#fff", fontFamily: "'Inter', sans-serif", width: "100%", resize: multiline ? "vertical" : "none", minHeight: multiline ? 56 : undefined, outline: "none" };
  if (editing) return multiline
    ? <textarea ref={ref} value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Escape") { setVal(value); setEditing(false); } }} style={sh} />
    : <input ref={ref} value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(value); setEditing(false); } }} style={sh} />;
  return (
    <span onClick={() => setEditing(true)} title="Edit" style={{ ...style, cursor: "text", borderBottom: `1px dashed ${T.border}` }}>
      {value || <span style={{ color: T.mute, fontStyle: "italic" }}>{placeholder}</span>}
    </span>
  );
}

/* ── Country code picker (TR, FR, DE…) ── */
export function CountryPicker({ flags, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = c => {
    if (c === "—") { onChange(["—"]); setOpen(false); return; }
    const cur = flags.filter(x => x !== "—");
    const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
    onChange(next.length ? next : ["—"]);
  };
  const display = flags.filter(f => f !== "—").join(" · ") || "—";
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span onClick={() => setOpen(!open)} style={{ ...mono, color: T.mute, cursor: "pointer", borderBottom: `1px dashed ${T.border}` }}>
        {display}
      </span>
      {open && (
        <div style={{ position: "absolute", top: "120%", left: 0, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 3, padding: 12, zIndex: 300, display: "flex", flexWrap: "wrap", gap: 6, width: 260, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          {COUNTRY_CODES.map(c => (
            <span key={c} onClick={() => toggle(c)} style={{ ...mono, fontSize: 10, cursor: "pointer", padding: "3px 6px", borderRadius: 2, background: flags.includes(c) ? T.ink : T.bg, color: flags.includes(c) ? "#fff" : T.mute, transition: "all .1s" }}>{c}</span>
          ))}
          <span onClick={() => toggle("—")} style={{ ...mono, fontSize: 10, cursor: "pointer", padding: "3px 6px", borderRadius: 2, background: T.bg, color: T.mute }}>CLR</span>
        </div>
      )}
    </div>
  );
}

/* ── Status picker ── */
export function StatusPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span onClick={() => setOpen(!open)} style={{ ...mono, color: T.mute, cursor: "pointer", borderBottom: `1px dashed ${T.border}`, paddingBottom: 1 }}>{value}</span>
      {open && (
        <div style={{ position: "absolute", top: "130%", left: 0, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 3, zIndex: 300, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", overflow: "hidden", minWidth: 160 }}>
          {STATUS_OPTIONS.map(s => (
            <div key={s} onClick={() => { onChange(s); setOpen(false); }}
              style={{ padding: "8px 12px", fontSize: 12, cursor: "pointer", background: s === value ? T.bg : "#fff", fontWeight: s === value ? 600 : 400, color: T.ink }}
              onMouseEnter={e => e.currentTarget.style.background = T.bg}
              onMouseLeave={e => e.currentTarget.style.background = s === value ? T.bg : "#fff"}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Phase mover ── */
export function PhaseMover({ value, onChange, filmTitle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const dot = PHASE_DOT[value] || T.mute;
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 5 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />
        <span style={{ ...mono, color: T.mute }}>{value}</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "130%", right: 0, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 3, zIndex: 300, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", overflow: "hidden", minWidth: 180 }}>
          <div style={{ padding: "6px 12px 4px", ...mono, fontSize: 9, color: T.mute }}>Move "{filmTitle}"</div>
          {PHASES.map(p => (
            <div key={p} onClick={() => { onChange(p); setOpen(false); }}
              style={{ padding: "8px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: p === value ? T.bg : "#fff" }}
              onMouseEnter={e => e.currentTarget.style.background = T.bg}
              onMouseLeave={e => e.currentTarget.style.background = p === value ? T.bg : "#fff"}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: PHASE_DOT[p], flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: p === value ? 600 : 400, color: T.ink }}>{p}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
